import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import environment from "../../environment/environment";
import "./UpcomingTrips.css";

const CurrencyFormatter = new Intl.NumberFormat("en-IN");
const formatINR = (value) => `₹${CurrencyFormatter.format(value)}`;

const MONTH_ABBR = {
  jan: "JAN", feb: "FEB", mar: "MAR", apr: "APR", may: "MAY", jun: "JUN",
  jul: "JUL", aug: "AUG", sep: "SEP", oct: "OCT", nov: "NOV", dec: "DEC",
};

// "Aug-2026" -> "AUG '26"
function formatMonthLabel(monthKey) {
  const [mon, year] = monthKey.split("-");
  const abbr = MONTH_ABBR[mon?.slice(0, 3).toLowerCase()] || (mon || "").toUpperCase();
  return `${abbr} '${(year || "").slice(-2)}`;
}

// "Aug-2026" -> sortable Date
function monthKeyToDate(monthKey) {
  const [mon, year] = monthKey.split("-");
  return new Date(`01 ${mon} ${year}`);
}

// Pull every FUTURE (>= today) departure date across all departure cities, dedupe, sort.
// Past dates are excluded entirely so they never show up and never get counted.
function extractDates(pkg) {
  const seen = new Map();

  const today = new Date();
  today.setHours(0, 0, 0, 0); // normalize so "today" itself still counts as upcoming

  (pkg.departureDetails || []).forEach((dep) => {
    (dep.packageDateDetails || []).forEach((d) => {
      const key = d.fullDate || `${d.packageDate}-${d.packageMonth}-${d.packageYear}`;
      if (!seen.has(key)) {
        const sortKey = new Date(`${d.packageDate} ${d.packageMonth} ${d.packageYear}`);

        // Skip anything before today — only current/upcoming dates are kept.
        if (sortKey >= today) {
          seen.set(key, {
            label: `${d.packageDate} ${(d.packageMonth || "").slice(0, 3)}`,
            sortKey,
          });
        }
      }
    });
  });

  return Array.from(seen.values()).sort((a, b) => a.sortKey - b.sortKey);
}

// Raw API package item -> the shape TripCard renders.
function mapPackageToTrip(pkg) {
  const allDates = extractDates(pkg); // already upcoming-only

  // Show only the single next upcoming date.
  const shownDates = allDates.slice(0, 1).map((d) => d.label);

  // Remaining upcoming batches beyond the one shown (past dates never counted, they were filtered above).
  const extraBatches = Math.max(allDates.length - shownDates.length, 0);

  const cities = Array.from(
    new Set((pkg.departureDetails || []).map((d) => d.cityName).filter(Boolean))
  );

  return {
    id: pkg.packageUID,
    image: pkg.imageList?.[0]?.coverImage,
    title: (pkg.packageName || "").trim(),
    originalPrice:
      pkg.packagePrices && pkg.packagePrices !== pkg.packageWithDiscountPrices
        ? pkg.packagePrices
        : undefined,
    price: pkg.packageWithDiscountPrices ?? pkg.packagePrices ?? 0,
    duration: `${pkg.packageNight}N / ${pkg.packageDays}D`,
    location: cities.length ? `${cities.join(" / ")} Departure` : pkg.routeName || "",
    dates: shownDates,
    extraBatches: extraBatches || undefined,
    raw: pkg,
  };
}

// Raw `packagesList` object -> months array the carousel renders.
function buildMonthsFromApiData(packagesList) {
  if (!packagesList || typeof packagesList !== "object") return [];
  return Object.keys(packagesList)
    .sort((a, b) => monthKeyToDate(a) - monthKeyToDate(b))
    .map((monthKey) => ({
      id: monthKey,
      label: formatMonthLabel(monthKey),
      trips: (packagesList[monthKey] || []).map(mapPackageToTrip),
    }));
}

// ---- Small inline icons (no external icon dependency) ----
const ClockIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="7.25" stroke="currentColor" strokeWidth="1.4" />
    <path
      d="M10 6v4.2l2.6 1.6"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PinIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M10 18s6-5.2 6-9.8A6 6 0 1 0 4 8.2C4 12.8 10 18 10 18Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <circle cx="10" cy="8.2" r="2" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <rect
      x="3.25"
      y="4.25"
      width="13.5"
      height="12.5"
      rx="1.75"
      stroke="currentColor"
      strokeWidth="1.4"
    />
    <path
      d="M3.25 8h13.5M6.75 2.75v3M13.25 2.75v3"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

const ChevronIcon = ({ direction = "left" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    style={{
      transform: direction === "right" ? "rotate(180deg)" : "none",
    }}
  >
    <path
      d="M15 6l-6 6 6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function TripCard({ trip, onClick }) {
  const {
    image,
    title,
    originalPrice,
    price,
    duration,
    location,
    dates = [],
    extraBatches,
  } = trip;

  return (
    <div
      className="ut-card"
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(trip)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick?.(trip);
      }}
    >
      <div className="ut-card__media">
        {image && <img src={image} alt={title} loading="lazy" />}
        <div className="ut-card__scrim" aria-hidden="true" />
        <span className="ut-card__price-badge">
          {originalPrice ? (
            <span className="ut-card__price-original">
              {formatINR(originalPrice)}
            </span>
          ) : null}
          <span className="ut-card__price-current">{formatINR(price)}</span>
          <span className="ut-card__price-suffix">Onwards</span>
        </span>
      </div>

      <div className="ut-card__body">
        <h3 className="ut-card__title">{title}</h3>

        <div className="ut-card__meta-row">
          <span className="ut-card__meta">
            <ClockIcon />
            {duration}
          </span>
          {location ? (
            <span className="ut-card__meta ut-card__meta--location">
              <PinIcon />
              <span className="ut-card__meta-text">{location}</span>
            </span>
          ) : null}
        </div>

        {dates.length > 0 && (
          <div className="ut-card__meta-row">
            <span className="ut-card__meta">
              <CalendarIcon />
              {dates.join(", ")}
              {extraBatches ? (
                <span className="ut-card__extra-batches">
                  {" "}
                  +{extraBatches} batches
                </span>
              ) : null}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function UpcomingTrips({
  data,
  months: monthsProp,
  title = "Upcoming Community Trips",
  onCardClick,
}) {
  const navigate = useNavigate();
  const [upcomingPackagesList, setUpcomingPackagesList] = useState({});

  const getUpcomingTourPackages = () => {
    axios({
      url: `${environment.config.apiBaseUrl}package/getUpcomingTourPackage`,
      method: "GET",
      headers: {
        authorization: "your token comes here",
      },
    })
      .then((res) => {
        if (res && res.data && res.data.packagesList) {
          setUpcomingPackagesList(res.data.packagesList);
        }
      })
      .catch((err) => { });
  }

  useEffect(() => {
    // Only self-fetch when the parent hasn't handed data down directly.
    if (!data && !monthsProp) {
      getUpcomingTourPackages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const months = useMemo(() => {
    if (monthsProp) return monthsProp;
    if (data) return buildMonthsFromApiData(data);
    return buildMonthsFromApiData(upcomingPackagesList);
  }, [monthsProp, data, upcomingPackagesList]);

  const [activeMonthId, setActiveMonthId] = useState(months[0]?.id);
  const trackRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  
  useEffect(() => {
    if (months.length && !months.some((m) => m.id === activeMonthId)) {
      setActiveMonthId(months[0].id);
    }
  }, [months, activeMonthId]);

  const activeMonth = months.find((m) => m.id === activeMonthId) || months[0];
  const trips = activeMonth?.trips || [];

  const updateArrowVisibility = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setShowLeftArrow(el.scrollLeft > 8);
    setShowRightArrow(el.scrollLeft < maxScroll - 8);
  }, []);

  useEffect(() => {
    updateArrowVisibility();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrowVisibility, { passive: true });
    window.addEventListener("resize", updateArrowVisibility);
    return () => {
      el.removeEventListener("scroll", updateArrowVisibility);
      window.removeEventListener("resize", updateArrowVisibility);
    };
  }, [updateArrowVisibility, trips]);

  useEffect(() => {
    if (trackRef.current) {
      trackRef.current.scrollTo({ left: 0 });
    }
  }, [activeMonthId]);

  const scrollByAmount = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    const cardWidth = el.querySelector(".ut-card")?.offsetWidth || 300;
    const gap = 20;
    const delta = (cardWidth + gap) * direction;
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  const handleCardClick = (trip) => {
    if (onCardClick) {
      onCardClick(trip.raw || trip);
      return;
    }
    // Default: same route pattern used elsewhere in HomeComponent.js
    navigate(`/package-details/:${trip.id}`);
  };

  if (!months.length) return null;

  return (
    <section className="ut-section" aria-label={title}>
      <div className="ut-section__inner">
        <h2 className="ut-section__title">{title}</h2>

        <div
          className="ut-month-strip"
          role="tablist"
          aria-label="Filter trips by month"
        >
          {months.map((month) => (
            <button
              key={month.id}
              type="button"
              role="tab"
              aria-selected={month.id === activeMonthId}
              className={
                "ut-month-chip" +
                (month.id === activeMonthId ? " ut-month-chip--active" : "")
              }
              onClick={() => setActiveMonthId(month.id)}
            >
              {month.label}
            </button>
          ))}
        </div>

        <div className="ut-carousel">
          {showLeftArrow && (
            <button
              type="button"
              className="ut-carousel__nav ut-carousel__nav--left"
              aria-label="Scroll left"
              onClick={() => scrollByAmount(-1)}
            >
              <ChevronIcon direction="left" />
            </button>
          )}

          {trips.length > 0 ? (
            <div className="ut-carousel__track" ref={trackRef}>
              {trips.map((trip) => (
                <TripCard key={trip.id} trip={trip} onClick={handleCardClick} />
              ))}
            </div>
          ) : (
            <div className="ut-empty">
              No trips scheduled for {activeMonth?.label} yet. Check back
              soon!
            </div>
          )}

          {showRightArrow && (
            <button
              type="button"
              className="ut-carousel__nav ut-carousel__nav--right"
              aria-label="Scroll right"
              onClick={() => scrollByAmount(1)}
            >
              <ChevronIcon direction="right" />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

export { buildMonthsFromApiData };